import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import "react-reflex/styles.css";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import CreateRecordButton from "../input/CreateRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import MachineTypeEmbed from "./MachineTypeEmbed";
import layoutConf from "../../config/layoutConf";

/**
 * @COMPONENT_NAME MachineTypeState
 * @DESCRIPTION 설비 유형과 설비 유형별 상태목록을 관리하는 화면이며 Reflex를 활용하여 분할되었음
 *              설비 유형은 MachineTypeEmbed를 활용하여 좌측에 배치하고 설비 유형별 상태목록은 오른쪽에 배치
 * 
 * @PROPS
 *    - 해당 컴포넌트는 props를 받지않음
 * @STATE
 *    - {Object} schema - 테이블 스키마 정보
 *    - {Object} grid - 그리드 객체 참조
 *    - {Object} api - API 객체 참조
 *    - {Object} selectGrid - 선택된 그리드 객체
 *    - {number} height - 컴포넌트 높이 값
 * @PASSTHROUGH_PROPS
 *    - {string} id - BasicCrudScreenType에 전달되는 테이블 식별자
 *    - {string} table - BasicCrudScreenType에 전달되는 테이블 이름
 *    - {string} view - BasicCrudScreenType에 전달되는 뷰 이름
 *    - {string} headerTitle - BasicCrudScreenType에 전달되는 헤더 이름
 *    - {Function} onModifyViewUrl = ViewUrl 변경 콜백 함수
 *    - {Function} onApiInitialized - API 초기화 시 호출되는 콜백 함수
 *    - {Function} onInitialized - 그리드 초기화 시 호출되는 콜백 함수
 *    - {Function} onAppendButtonInFrontOfSearch - 검색 영역 앞에 버튼 추가 함수
 *
 * @CALLBACKS
 *    - {Function} onSuccess - 작업 성공 시 알림을 표시하는 함수
 *    - {Function} onFailure - 작업 실패 시 오류 알림을 표시하는 함수
 *    - {Function} onNotifyWarning - 경고 알림을 표시하는 함수
 *    - {Function} notifySuccess - 성공 알림
 *    - {Function} notifyFailure - 실패 알림
 *    - {Function} notifyWarning - 경고 알림
 *    - {Function} handleMachineTypeSelect - MachineType 그리드 선택시 호출되는 콜백 함수
 */

function MachineTypeState(props) {

    const id = "MachineTypeState";

    const [height, setHeight] = useState(0);
    const [schema, setSchema] = useState();
    const [api, setApi] = useState();
    const [grid, setGrid] = useState();
    const [selectGrid, setSelectGrid] = useState();
    const [selectedMachineType, setSelectedMachineType] = useState();

    const { t } = useTranslation();
    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let mounted = true;
        async function schemaSetting() {
            let schema = await getSchema("MachineTypeState");
            if (mounted) {
                setSchema(schema);
            }
        }

        schemaSetting();
        return (() => {
            mounted = false;
        });
    }, []);

    useEffect(() => {
        (async () => {
            if (grid) {
                grid.selectionChanged.addHandler((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        setSelectGrid(grid.selectedItems[0]);
                    } else {
                        setSelectGrid(undefined);
                    };
                });

                grid.itemsSourceChanged.addHandler((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        setSelectGrid(grid.selectedItems[0]);
                    } else {
                        setSelectGrid(undefined);
                    };
                });
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grid]);

    useEffect(() => {
        setApi((api) => {
            if (api) {
                api.refresh();
            }

            return api;
        });
    }, [selectedMachineType]);

    const handleMachineTypeSelect = (machineType) => {
        setSelectedMachineType(machineType && machineType.length !== 0 ? machineType : null);
    };

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="vertical">
                <ReflexElement flex={0.5}>
                    <MachineTypeEmbed onSelect={handleMachineTypeSelect} />
                </ReflexElement>

                <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />
                <ReflexElement flex={0.5}>
                <BasicCrudScreenType
                    id={id}
                    headerTitle={t("term.machineTypeState")}
                    table="MachineTypeState"
                    view="VwMachineTypeState"
                    onModifyViewUrl={(url) => `${url}/${selectedMachineType?.machineTypeCode}`}
                    onApiInitialized={onApiInitialized}
                    onInitialized={onInitialized}
                    noCreate
                    noEdit
                    noDelete
                    noExcel
                    onAppendButtonInFrontOfSearch={() => (
                        <>
                            <BasicCrudScreenActionItemLayout>
                                <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                    <CreateRecordButton
                                        id={id}
                                        schema={schema}
                                        refresh={api}
                                        disabled={!selectedMachineType}
                                        setInitialDataCallback={(formData)=>  {
                                            formData["machineTypeCode"] = selectedMachineType?.machineTypeCode;
                                        }}
                                        onSuccess={onSuccess}
                                    />
                                </AccessControl>
                            </BasicCrudScreenActionItemLayout>
                            <BasicCrudScreenActionItemLayout>
                                <AccessControl accessTarget={schema && schema.name} accessMethod={schema && (schema.updateWithPatch ? "PATCH" : "PUT")}>
                                    <UpdateRecordButton
                                        id={id}
                                        schema={schema}
                                        grid={grid}
                                        refresh={api}
                                        disabled={selectGrid ? false : true}
                                        onSuccess={notifySuccess}
                                    />
                                </AccessControl>
                            </BasicCrudScreenActionItemLayout>
                            <BasicCrudScreenActionItemLayout>
                                <AccessControl accessTarget={schema && schema.name} accessMethod="DELETE">
                                    <DeleteRecordButton
                                        id={id}
                                        schema={schema}
                                        grid={grid}
                                        refresh={api}
                                        color="secondary"
                                        onSuccess={onSuccess}
                                        onFailure={onFailure}
                                        onPartialFailure={onNotifyWarning}
                                        disabled={selectGrid ? false : true}
                                    />
                                </AccessControl>
                            </BasicCrudScreenActionItemLayout>
                        </>
                    )}
                />
            </ReflexElement>
        </ReflexContainer>
        </div >
    );
}

export default MachineTypeState;
